import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { AddResume, AddResumeCertification, AddResumeDocument, AddResumeEducation, AddResumeEmployment, AddResumeLanguage, AddResumeReference, AddResumeSkill } from '../models/resume/add-resume';
import { Observable } from 'rxjs';
import { GetResumeCandidatePersonalInfo, GetResumeDetail, Resume, ResumeCertification, ResumeDocument, ResumeEducation, ResumeEmployment, ResumeLanguage, ResumeReference, ResumeSkill } from '../models/resume/resume';
import { HttpUtil } from '../utils/http-util';

@Injectable({
  providedIn: 'root'
})
export class ResumeService {

  constructor(
    private _http: HttpClient
  ) { }

  public getResumes(): Observable<Resume[]>{
    return this._http.get<Resume[]>(`${environment.api.url}/Resume`);
  }

  public getResumeById(resumeId: number): Observable<Resume>{
    return this._http.get<Resume>(`${environment.api.url}/Resume/${resumeId}`);
  }

  public getResumeDetail(resumeId: number): Observable<GetResumeDetail>{
    return this._http.get<GetResumeDetail>(`${environment.api.url}/Resume/Detail/${resumeId}`);
  }

  public deleteResume(resumeId: number): Observable<boolean> {
    return this._http.delete<any>(`${environment.api.url}/Resume/${resumeId}`);
  }

  public addResume(role: AddResume): Observable<boolean> {
    return this._http.post<any>(`${environment.api.url}/Resume`, role);
  }

  public addResumeEducation(resumeEducation: AddResumeEducation): Observable<boolean> {
    return this._http.post<any>(`${environment.api.url}/Resume/education`, resumeEducation);
  }

  public getResumeEducations(resumeId: number): Observable<ResumeEducation[]>{
    return this._http.get<ResumeEducation[]>(`${environment.api.url}/Resume/education/${resumeId}`);
  }

  public deleteResumeEducation(resumeEducationId: number): Observable<boolean> {
    return this._http.delete<any>(`${environment.api.url}/Resume/education/${resumeEducationId}`);
  }

  public getResumeSkills(resumeId: number, softSkill: boolean = false): Observable<ResumeSkill[]>{
    return this._http.get<ResumeSkill[]>(`${environment.api.url}/Resume/skill/${resumeId}/${softSkill}`);
  }

  public addResumeSkill(resumeSkill: AddResumeSkill): Observable<boolean> {
    return this._http.post<any>(`${environment.api.url}/Resume/skill`, resumeSkill);
  }

  public deleteResumeSkill(resumeSkillId: number): Observable<boolean> {
    return this._http.delete<any>(`${environment.api.url}/Resume/skill/${resumeSkillId}`);
  }

  public getResumeLanguages(resumeId: number): Observable<ResumeLanguage[]>{
    return this._http.get<ResumeLanguage[]>(`${environment.api.url}/Resume/Language/${resumeId}`);
  }

  public addResumeLanguage(resumeLanguage: AddResumeLanguage): Observable<boolean> {
    return this._http.post<any>(`${environment.api.url}/Resume/Language`, resumeLanguage);
  }

  public deleteResumeLanguage(resumeLanguageId: number): Observable<boolean> {
    return this._http.delete<any>(`${environment.api.url}/Resume/Language/${resumeLanguageId}`);
  }

  public getResumeEmployments(resumeId: number): Observable<ResumeEmployment[]>{
    return this._http.get<ResumeEmployment[]>(`${environment.api.url}/Resume/Employment/${resumeId}`);
  }

  public addResumeEmployment(resumeEmployment: AddResumeEmployment): Observable<boolean> {
    return this._http.post<any>(`${environment.api.url}/Resume/Employment`, resumeEmployment);
  }

  public deleteResumeEmployment(resumeEmploymentId: number): Observable<boolean> {
    return this._http.delete<any>(`${environment.api.url}/Resume/Employment/${resumeEmploymentId}`);
  }

  public getResumeCertifications(resumeId: number): Observable<ResumeCertification[]>{
    return this._http.get<ResumeCertification[]>(`${environment.api.url}/Resume/Certification/${resumeId}`);
  }

  public addResumeCertification(resumeCertification: AddResumeCertification): Observable<boolean> {
    return this._http.post<any>(`${environment.api.url}/Resume/Certification`, resumeCertification);
  }

  public deleteResumeCertification(resumeCertificationId: number): Observable<boolean> {
    return this._http.delete<any>(`${environment.api.url}/Resume/Certification/${resumeCertificationId}`);
  }

  public addResumeDocument(resumeDocument: AddResumeDocument): Observable<boolean> {
    return this._http.post<any>(`${environment.api.url}/Resume/document`, resumeDocument);
  }

  public getResumeDocument(resumeId: number | null): Observable<ResumeDocument[]>{
    return this._http.get<ResumeDocument[]>(`${environment.api.url}/Resume/documents`, {
      params: HttpUtil.toHttpParams({
        resumeId: resumeId
      })
    });
  }

  public deleteResumeDocument(resumeDocumentId: number): Observable<boolean> {
    return this._http.delete<any>(`${environment.api.url}/Resume/document/${resumeDocumentId}`);
  }

  public getResumeReferences(resumeId: number): Observable<ResumeReference[]>{
    return this._http.get<ResumeReference[]>(`${environment.api.url}/Resume/reference/${resumeId}`);
  }

  public addResumeReference(resumeReference: AddResumeReference): Observable<boolean> {
    return this._http.post<any>(`${environment.api.url}/Resume/reference`, resumeReference);
  }

  public deleteResumeReference(resumeReferenceId: number): Observable<boolean> {
    return this._http.delete<any>(`${environment.api.url}/Resume/reference/${resumeReferenceId}`);
  }

  public getResumeCandidatePersonalInfo(){
    return this._http.get<GetResumeCandidatePersonalInfo>(`${environment.api.url}/Resume/candidatePersonalInfo`);
  }

  public generateResumePdf(resumeId: number){
    return this._http.get(`${environment.api.url}/Resume/generatePdf/${resumeId}`, { responseType: 'blob' });
  }

}